import { BusyIndicator, Toolbar } from "client/Components";
import {
	ReactElement,
	useEffect,
	useState
} from "react";
import { AnalyticsCard } from "./AnalyticsCard";
import { localize } from "client_legacy/ndaw/culture";
import {
	AppUsageStatisticModel,
	AppUsageStatisticsSummary,
	getActiveUsersCount,
	getAppUsage
} from "client/Api/Analytics/analyticsStatistics";
import { isNil } from "client/Misc/utils";
import { AppUsageDialog } from "./AppUsageDialog";
import {
	daysRangeOptions,
	StatisticsFormatter,
	useStatisticsFormatters
} from "./common";
import { AlertManager, useAlerts } from "client/Hooks";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import { AnalyticsTabLayout } from "./AnalyticsTabLayout";
import { AnalyticsShortList } from "./AnalyticsShortList";

const TOP_USED_APP_COUNT = 3;

export type AppsAndUsersTabProps = {
	selectedDaysRange: number;
	onSelectedDaysRangeChange: (selectedDaysRange: number) => void;
};

export const AppsAndUsersTab = (props: AppsAndUsersTabProps): ReactElement => {
	const {
		selectedDaysRange,
		onSelectedDaysRangeChange
	} = props;

	const [isBusy, setIsBusy] = useState<boolean>(false);
	const [isAppUsageDialogOpen, setIsAppUsageDialogOpen] = useState<boolean>(false);
	const [statistics, setStatistics] = useState<Statistics | undefined>(undefined);

	const alertManager: AlertManager = useAlerts();

	const formatter: StatisticsFormatter = useStatisticsFormatters();

	useEffect(
		(): EffectWithDestructorResult => {
			async function fetchData(signal: AbortSignal): Promise<void> {
				try {
					setIsBusy(true);

					const [activeUsersCount, appUsageSummary] = await Promise.all([
						getActiveUsersCount(selectedDaysRange, signal),
						getAppUsage(
							selectedDaysRange,
							TOP_USED_APP_COUNT,
							signal
						),
					]);

					setStatistics({
						appUsageSummary,
						activeUsersCount
					});

					setIsBusy(false);
				} catch (e) {
					if (isCancelledError(e)) {
						return;
					}

					setIsBusy(false);

					alertManager.showError(
						localize("_GeneralErrorMessage")
					);
				}
			}

			const cancellation = new AbortController();

			fetchData(cancellation.signal);
			return () => cancellation.abort();

		},
		[alertManager, selectedDaysRange]
	);

	return (
		<>
			<AnalyticsTabLayout>
				<AnalyticsTabLayout.ToolbarContainer>
					<Toolbar>
						<Toolbar.InlineOptionSelector
							value={selectedDaysRange}
							onChange={onSelectedDaysRangeChange}
							options={daysRangeOptions}
							dropOverAlignment="left"
							menuTitle={localize("_Range")}
						/>
					</Toolbar>
				</AnalyticsTabLayout.ToolbarContainer>
				{isBusy && <BusyIndicator />}

				{statistics && (
					<AnalyticsTabLayout.CardList>
						<AnalyticsCard>
							<AnalyticsCard.Title
								title={
									formatter(
										statistics.appUsageSummary
											.usedAppsCount,
										true
									)
								}
							>
								{formatter(
									statistics.appUsageSummary
										.usedAppsCount
								)}
							</AnalyticsCard.Title>
							<AnalyticsCard.Subtitle>
								{localize("_UsedApps")}
							</AnalyticsCard.Subtitle>
							<AnalyticsCard.Content>
								<AnalyticsShortList>
									{statistics.appUsageSummary
										.appUsage
										.map((x: AppUsageStatisticModel, i: number) => {
											const isDeletedApp: boolean = isNil(x.appId);

											return (
												<AnalyticsShortList.Item
													key={i}
													name={isDeletedApp ? localize("_DeletedApp") : x.appName}
													value={x.userCount}
													isDisabled={isDeletedApp}
												/>
											);
										})
									}
								</AnalyticsShortList>
							</AnalyticsCard.Content>
							{statistics
								.appUsageSummary
								.appUsage
								.length > 0 && (
									<AnalyticsCard.Footer
										title={localize("_ViewAll")}
										onClick={() => setIsAppUsageDialogOpen(true)}
									/>
								)}
						</AnalyticsCard>

						<AnalyticsCard>
							<AnalyticsCard.Title
								title={formatter(statistics.activeUsersCount, true)}
							>
								{formatter(statistics.activeUsersCount)}
							</AnalyticsCard.Title>
							<AnalyticsCard.Subtitle>
								{localize("_ActiveUsers")}
							</AnalyticsCard.Subtitle>
						</AnalyticsCard>
					</AnalyticsTabLayout.CardList>
				)}
			</AnalyticsTabLayout>
			{isAppUsageDialogOpen && (
				<AppUsageDialog
					onClose={() => setIsAppUsageDialogOpen(false)}
					selectedDaysRange={selectedDaysRange}
				/>
			)}
		</>
	);
};
AppsAndUsersTab.displayName = "AppsAndUsersTab";

type Statistics = {
	activeUsersCount: number;
	appUsageSummary: AppUsageStatisticsSummary;
};
